// Web Audio synthesized sound engine for Crossbow Chaos - no external files.
import { gameStore } from "@/hooks/useGameStore";

let ctx = null;
let master = null;

function ensureCtx() {
  if (!ctx) {
    const C = window.AudioContext || window.webkitAudioContext;
    if (!C) return null;
    ctx = new C();
    master = ctx.createGain();
    master.gain.value = 0.35;
    master.connect(ctx.destination);
  }
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
}

function enabled() {
  try { return gameStore.get().settings.sound !== false; } catch { return true; }
}

function envGain(start, peak, decay, when) {
  const g = ctx.createGain();
  g.gain.setValueAtTime(start, when);
  g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0001), when + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, when + decay);
  return g;
}

function tone({ freq = 440, type = "sine", dur = 0.2, vol = 0.3, sweepTo = null, delay = 0 }) {
  if (!enabled() || !ensureCtx()) return;
  const t0 = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (sweepTo !== null) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(sweepTo, 20), t0 + dur);
  }
  const g = envGain(0.0001, vol, dur, t0);
  osc.connect(g).connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

function noiseBurst({ dur = 0.15, vol = 0.25, delay = 0, type = "highpass", freq = 1200 }) {
  if (!enabled() || !ensureCtx()) return;
  const t0 = ctx.currentTime + delay;
  const bufferSize = Math.floor(ctx.sampleRate * dur);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = type;
  filter.frequency.value = freq;
  const g = envGain(0.0001, vol, dur, t0);
  src.connect(filter).connect(g).connect(master);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

export const audio = {
  unlock: () => ensureCtx(),
  shoot: () => {
    tone({ freq: 900, type: "square", dur: 0.08, vol: 0.18, sweepTo: 220 });
    noiseBurst({ dur: 0.06, vol: 0.12, freq: 2000 });
  },
  hit: (diff = "easy") => {
    const map = { easy: 500, medium: 700, hard: 900 };
    tone({ freq: map[diff] || 500, type: "triangle", dur: 0.18, vol: 0.3, sweepTo: 220 });
    noiseBurst({ dur: 0.1, vol: 0.15, freq: 1500 });
  },
  miss: () => {
    tone({ freq: 180, type: "sawtooth", dur: 0.15, vol: 0.12, sweepTo: 90 });
  },
  coin: () => {
    tone({ freq: 1200, type: "sine", dur: 0.07, vol: 0.22 });
    tone({ freq: 1800, type: "sine", dur: 0.09, vol: 0.22, delay: 0.06 });
  },
  funny: () => {
    // silly boing
    tone({ freq: 220, type: "sawtooth", dur: 0.2, vol: 0.18, sweepTo: 900 });
  },
  victory: () => {
    const notes = [523, 659, 784, 1047, 1319];
    notes.forEach((n, i) =>
      tone({ freq: n, type: "square", dur: 0.18, vol: 0.22, delay: i * 0.1 })
    );
    // sparkle
    for (let i = 0; i < 6; i++) {
      tone({ freq: 1600 + Math.random() * 800, type: "sine", dur: 0.1, vol: 0.1, delay: 0.3 + i * 0.06 });
    }
  },
  defeat: () => {
    [440, 330, 260, 175].forEach((n, i) =>
      tone({ freq: n, type: "triangle", dur: 0.25, vol: 0.2, delay: i * 0.14 })
    );
  },
  countdown: () => tone({ freq: 880, type: "square", dur: 0.1, vol: 0.2 }),
  start: () => tone({ freq: 1200, type: "square", dur: 0.3, vol: 0.25 }),
  buy: () => {
    tone({ freq: 700, type: "triangle", dur: 0.1, vol: 0.25 });
    tone({ freq: 1050, type: "triangle", dur: 0.12, vol: 0.25, delay: 0.08 });
    tone({ freq: 1400, type: "triangle", dur: 0.14, vol: 0.25, delay: 0.16 });
  },
};
